/**
 * React Router 6 ルーティング設定
 *
 * createBrowserRouterでルート定義、React.lazy()でコード分割を実装
 */

import React, { Suspense } from "react";
import { createBrowserRouter, Outlet } from "react-router";
import { rootLoader } from "@/loaders/root-loader";
import { Splash } from "./components/shared/Splash";
import { ScrollToTop } from "./components/shared/ScrollToTop";
import { EnvironmentBanner } from "./components/shared/EnvironmentBanner";
import { SysadminGuard } from "./components/shared/SysadminGuard";

// ─────────────────────────────────────────────────────────────
// Lazy-loaded Pages
// ─────────────────────────────────────────────────────────────

const Recording = React.lazy(() => import("./pages/Recording/index"));
const SpeedRecording = React.lazy(() => import("./pages/SpeedRecording/index"));
const CopyRecordSetup = React.lazy(() => import("./pages/CopyRecordSetup/index"));
const Dashboard = React.lazy(() => import("./pages/Dashboard/index"));
const MeetingDetail = React.lazy(() => import("./pages/MeetingDetail/index"));
const MeetingGroupCreate = React.lazy(
  () => import("./pages/MeetingGroupCreate"),
);
const MeetingGroupDetail = React.lazy(
  () => import("./pages/MeetingGroupDetail"),
);
const Guide = React.lazy(() => import("./pages/Guide/index"));
const GuideArticleDetail = React.lazy(
  () => import("./pages/Guide/ArticleDetail"),
);
const ReleaseNotes = React.lazy(
  () => import("./pages/ReleaseNotes/index"),
);
const Announcements = React.lazy(
  () => import("./pages/Announcements/index"),
);
const Trash = React.lazy(() => import("./pages/Trash/index"));
const Notifications = React.lazy(() => import("./pages/Notifications/index"));
const Settings = React.lazy(() => import("./pages/Settings/index"));
const AdminLayout = React.lazy(() => import("./components/shared/AdminLayout"));
const AdminTop = React.lazy(() => import("./pages/admin/AdminTop"));
const Users = React.lazy(() => import("./pages/admin/Users"));
const UserDetail = React.lazy(() => import("./pages/admin/Users/[userId]"));
const TenantSettings = React.lazy(() => import("./pages/admin/TenantSettings"));
const AdminPresets = React.lazy(() => import("./pages/admin/Presets"));
const AdminPresetDetail = React.lazy(() => import("./pages/admin/Presets/[presetId]"));
const AdminCompanies = React.lazy(() => import("./pages/admin/Companies"));
const AdminCompanyDetail = React.lazy(() => import("./pages/admin/Companies/[companyId]"));
const CompanyReport = React.lazy(() => import("./pages/admin/Companies/[companyId]/Report"));
const AdminDictionaries = React.lazy(() => import("./pages/admin/Dictionaries"));
const AdminDictionaryDetail = React.lazy(() => import("./pages/admin/Dictionaries/[dictionaryId]"));
const AdminRealtimeConfigs = React.lazy(() => import("./pages/admin/RealtimeConfigs"));
const AdminRealtimeConfigDetail = React.lazy(() => import("./pages/admin/RealtimeConfigs/[configId]"));
const Forbidden = React.lazy(() => import("./pages/403"));
const NotFound = React.lazy(() => import("./pages/404"));

// ─────────────────────────────────────────────────────────────
// Layout Component
// ─────────────────────────────────────────────────────────────
// eslint-disable-next-line react-refresh/only-export-components
const RootLayout: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <EnvironmentBanner />
      <ScrollToTop />
      <Suspense fallback={<Splash />}>
        <Outlet />
      </Suspense>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Router Configuration
// ─────────────────────────────────────────────────────────────

export const router = createBrowserRouter([
  {
    path: import.meta.env.BASE_URL,
    id: "root",
    loader: rootLoader,
    element: <RootLayout />,
    children: [
      { index: true, element: <Dashboard /> },
      { path: "recording", element: <Recording /> },
      { path: "speed-recording", element: <SpeedRecording /> },
      { path: "copy-record-setup", element: <CopyRecordSetup /> },
      { path: "meetings/:id", element: <MeetingDetail /> },
      { path: "meeting-groups/create", element: <MeetingGroupCreate /> },
      { path: "meeting-groups/:groupId", element: <MeetingGroupDetail /> },
      { path: "guide", element: <Guide /> },
      {
        path: "guide/article/:categoryId/:articleId",
        element: <GuideArticleDetail />,
      },
      { path: "release-notes", element: <ReleaseNotes /> },
      { path: "announcements", element: <Announcements /> },
      { path: "trash", element: <Trash /> },
      { path: "notifications", element: <Notifications /> },
      { path: "settings", element: <Settings /> },
      // メンバー向け企業マスタ管理（admin/companiesと同じコンポーネントを共有）
      { path: "companies", element: <AdminCompanies /> },
      { path: "companies/:companyId", element: <AdminCompanyDetail /> },
      {
        path: "admin",
        element: <AdminLayout />,
        children: [
          { index: true, element: <AdminTop /> },
          // admin向け（一般管理者でもアクセス可能）
          { path: "companies", element: <AdminCompanies /> },
          { path: "companies/:companyId", element: <AdminCompanyDetail /> },
          { path: "companies/:companyId/report", element: <CompanyReport /> },
          { path: "dictionaries", element: <AdminDictionaries /> },
          { path: "dictionaries/:dictionaryId", element: <AdminDictionaryDetail /> },
          // sysadmin限定
          {
            element: <SysadminGuard />,
            children: [
              { path: "users", element: <Users /> },
              { path: "users/:userId", element: <UserDetail /> },
              { path: "tenant-settings", element: <TenantSettings /> },
              { path: "presets", element: <AdminPresets /> },
              { path: "presets/new", element: <AdminPresetDetail /> },
              { path: "presets/:presetId", element: <AdminPresetDetail /> },
              { path: "realtime-configs", element: <AdminRealtimeConfigs /> },
              { path: "realtime-configs/new", element: <AdminRealtimeConfigDetail /> },
              { path: "realtime-configs/:configId", element: <AdminRealtimeConfigDetail /> },
            ],
          },
        ],
      },
      {
        path: "403",
        element: <Forbidden />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);
